import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ShoppingBag } from "lucide-react";
import { ImageLightbox, MagnifyButton } from "@/components/ImageLightbox";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export default function ImageViewer({ images, title, is_featured }) {

	const { t} = useTranslation();
	const [currentImageIndex, setCurrentImageIndex] = useState(0);
	const [lightboxOpen, setLightboxOpen] = useState(false);
	const selectedRef = useRef(null);
	const touchStartX = useRef(0);
	const touchEndX = useRef(0);

	useEffect(() => {
		if (selectedRef.current) {
			selectedRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
				inline: "center", // centers it horizontally in the slider
			});
		}
	}, [currentImageIndex]);

	const openLightbox = (index) => {
		setCurrentImageIndex(index);
		setLightboxOpen(true);
	};

	const nextImage = () => {
		setCurrentImageIndex((prev) => (prev + 1) % images.length);
	};

	const prevImage = () => {
		setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
	};

	const handleTouchStart = (e) => {
		touchStartX.current = e.touches[0].clientX;
	};

	const handleTouchMove = (e) => {
		touchEndX.current = e.touches[0].clientX;
	};

	const handleTouchEnd = () => {
		const swipeDistance = touchStartX.current - touchEndX.current;
		const minSwipeDistance = 50; // px threshold to count as a swipe

		if (swipeDistance > minSwipeDistance) {
			// Swiped left → next image
			nextImage();
		} else if (swipeDistance < -minSwipeDistance) {
			// Swiped right → previous image
			prevImage();
		}
	};

	return (
		<div>
			<motion.div
				initial={ { opacity: 0, x: -20 } }
				animate={ { opacity: 1, x: 0 } }
				transition={ { duration: 0.6 } }
				className="relative"
				onTouchStart={ handleTouchStart }
				onTouchMove={ handleTouchMove }
				onTouchEnd={ handleTouchEnd }
			>
				<div className="aspect-[16/10] bg-[#F2F0E9] rounded-sm overflow-hidden group relative"
						 onClick={ () => images.length > 0 ? openLightbox(currentImageIndex) : null }>
					{ images.length > 0 ? (
						<img
							src={ images[currentImageIndex].preview }
							alt={ title }
							className="w-full h-full object-cover cursor-pointer group-hover:scale-105 transition-transform duration-500"/>
					) : (
						<div className="w-full h-full flex items-center justify-center text-[#D4C5A9]">
							<ShoppingBag className="w-16 h-16"/>
						</div>
					) }
					{ is_featured && (
						<span	className="absolute top-4 left-4 featured-badge px-4 py-1.5 text-xs font-bold tracking-wider uppercase
								text-[#1A3C34] rounded-sm">
							{ t('horse.featured') }
						</span>
					) }
					{/* Magnify button */ }
					{ images.length > 0 &&
						<MagnifyButton
							onClick={ (e) => {
								e.stopPropagation();
								openLightbox(currentImageIndex);
							} }
							className="opacity-0 group-hover:opacity-100"
						/>
					}
					{/* Navigation Arrows */ }
					{ images.length > 1 && (
						<>
							<button
								onClick={ (e) => {
									e.stopPropagation();
									prevImage();
								} }
								className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full
									flex items-center justify-center transition-colors"
								data-testid="prev-image-btn"
							>
								<ChevronLeft className="w-6 h-6" strokeWidth={ 1.5 }/>
							</button>
							<button
								onClick={ (e) => {
									e.stopPropagation();
									nextImage();
								} }
								className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full
									flex items-center justify-center transition-colors"
								data-testid="next-image-btn"
							>
								<ChevronRight className="w-6 h-6" strokeWidth={ 1.5 }/>
							</button>
						</>
					) }
				</div>
				{/* Thumbnails */ }
				{ images.length > 1 && (
					<div className="flex gap-2 overflow-x-auto pb-2">
						{ images.map((img, i) => (
							<button key={ i } onClick={ () => setCurrentImageIndex(i) }
											className={ `shrink-0 w-20 h-20 rounded-sm overflow-hidden border-2 transition-colors 
												${ i === currentImageIndex ? 'border-[#8B4513]' : 'border-transparent' }` }>
								<img
									ref={ currentImageIndex === i ? selectedRef : null }
									src={ img.thumbnail || img.preview }
									alt=""
									className="w-full h-full object-cover"/>
							</button>
						)) }
					</div>
				) }
			</motion.div>
			{/* Image Lightbox */ }
			<ImageLightbox
				images={ images }
				initialIndex={ currentImageIndex }
				isOpen={ lightboxOpen }
				onClose={ () => setLightboxOpen(false) }
			/>
		</div>
	);
};